import { useEffect, useRef, useState } from "react";
import { createTicketSubject, getAllTicketSubjects } from "../services/ticketService";

interface Subject {
  id: string;
  name: string;
  description: string;
  createdAt?: string;
}

const NAME_MAX = 100;
const DESC_MAX = 500;

function formatDate(iso?: string) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("tr-TR", { day: "2-digit", month: "short", year: "numeric" });
}

export default function TicketSubjectsModal({ onClose }: { onClose: () => void }) {
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [search, setSearch] = useState("");

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [justCreatedId, setJustCreatedId] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);

  const load = () => {
    setLoading(true);
    setLoadError("");
    getAllTicketSubjects()
      .then((res) => setSubjects(res.data ?? []))
      .catch(() => setLoadError("Konular yüklenemedi."))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (showForm) setTimeout(() => nameRef.current?.focus(), 50);
  }, [showForm]);

  const resetForm = () => {
    setName("");
    setDescription("");
    setFormError("");
    setShowForm(false);
  };

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) { setFormError("Konu adı zorunludur."); return; }
    if (subjects.some((s) => s.name?.trim().toLowerCase() === trimmed.toLowerCase())) {
      setFormError("Bu isimde bir konu zaten var.");
      return;
    }
    setSaving(true);
    setFormError("");
    createTicketSubject({ name: trimmed, description: description.trim() || undefined })
      .then((res) => {
        setJustCreatedId(res.data?.id ?? null);
        resetForm();
        load();
      })
      .catch((err) => {
        const status = err?.response?.status;
        const desc = err?.response?.data?.[0]?.description;
        setFormError(
          status === 403 ? "Konu oluşturmak için SuperAdmin yetkisi gerekiyor."
            : desc ?? "Konu oluşturulamadı."
        );
      })
      .finally(() => setSaving(false));
  };

  const q = search.trim().toLowerCase();
  const filtered = subjects
    .filter((s) => !q || s.name?.toLowerCase().includes(q) || s.description?.toLowerCase().includes(q))
    .sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "", "tr"));

  const inp: React.CSSProperties = {
    width: "100%", borderRadius: 10, border: "1.5px solid #e0e7ff", padding: "9px 12px",
    fontSize: "0.88rem", color: "#111827", outline: "none", background: "#fff",
    fontFamily: "inherit", boxSizing: "border-box",
  };

  return (
    <>
      {!isMobile && (
        <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 2040, background: "rgba(15,23,42,0.5)", backdropFilter: "blur(3px)" }} />
      )}
      <div style={isMobile
        ? { position: "fixed", inset: 0, zIndex: 2050, background: "#fff", display: "flex", flexDirection: "column" }
        : {
            position: "fixed", top: "50%", left: "50%", transform: "translate(-50%,-50%)", zIndex: 2050,
            width: "min(620px, calc(100vw - 24px))", maxHeight: "88dvh", background: "#fff", borderRadius: 18,
            boxShadow: "0 24px 64px rgba(0,0,0,0.2)", display: "flex", flexDirection: "column", overflow: "hidden",
          }}>
        {/* Header */}
        <div style={{
          padding: isMobile ? "12px 14px" : "16px 20px", background: "linear-gradient(135deg,#4f46e5,#6366f1)",
          display: "flex", alignItems: "center", gap: 12, flexShrink: 0,
          paddingTop: isMobile ? "max(12px, env(safe-area-inset-top))" : undefined,
        }}>
          <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,0.18)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <i className="bi bi-tags-fill text-white"></i>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: "0.98rem", color: "#fff" }}>Ticket Konuları</div>
            <div style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.75)" }}>
              {loading ? "Yükleniyor…" : `${subjects.length} konu`}
            </div>
          </div>
          <button onClick={onClose} title="Kapat" style={{ background: "rgba(255,255,255,0.18)", border: "none", borderRadius: 9, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: "#fff", flexShrink: 0 }}>
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: isMobile ? "10px 14px" : "12px 20px", borderBottom: "1.5px solid #f1f5f9", flexShrink: 0 }}>
          <div style={{ position: "relative" }}>
            <i className="bi bi-search" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: "#9ca3af", fontSize: "0.85rem" }}></i>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Konu ara…" style={{ ...inp, paddingLeft: 34, background: "#f8faff" }} />
          </div>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: "auto", padding: isMobile ? "12px 14px" : "14px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
          {loading && (
            <div style={{ display: "flex", justifyContent: "center", padding: "32px 0" }}>
              <span className="spinner-border text-primary" />
            </div>
          )}
          {loadError && (
            <div style={{ padding: "10px 14px", borderRadius: 10, background: "#fef2f2", border: "1.5px solid #fecaca", color: "#991b1b", fontSize: "0.82rem", display: "flex", alignItems: "center", gap: 8 }}>
              <i className="bi bi-exclamation-circle-fill"></i>{loadError}
              <button onClick={load} style={{ marginLeft: "auto", background: "none", border: "none", color: "#dc2626", fontWeight: 700, cursor: "pointer", fontSize: "0.78rem" }}>Tekrar dene</button>
            </div>
          )}
          {!loading && !loadError && filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 0", color: "#9ca3af", fontSize: "0.85rem" }}>
              <i className="bi bi-tags" style={{ fontSize: "2rem", display: "block", marginBottom: 6, opacity: 0.4 }}></i>
              {q ? "Aramaya uygun konu bulunamadı." : "Henüz konu eklenmemiş."}
            </div>
          )}
          {!loading && filtered.map((s) => (
            <div key={s.id} style={{
              border: `1.5px solid ${s.id === justCreatedId ? "#a5b4fc" : "#e5e7eb"}`,
              background: s.id === justCreatedId ? "#eef2ff" : "#fff",
              borderRadius: 12, padding: "10px 14px", display: "flex", alignItems: "flex-start", gap: 10,
            }}>
              <div style={{ width: 32, height: 32, borderRadius: 9, background: "#eef2ff", color: "#4f46e5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <i className="bi bi-tag-fill" style={{ fontSize: "0.85rem" }}></i>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#111827", wordBreak: "break-word" }}>
                  {s.name}
                  {s.id === justCreatedId && (
                    <span style={{ marginLeft: 8, fontSize: "0.62rem", fontWeight: 700, color: "#4f46e5", background: "#e0e7ff", borderRadius: 10, padding: "1px 7px" }}>YENİ</span>
                  )}
                </div>
                {s.description && <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 2, wordBreak: "break-word" }}>{s.description}</div>}
                {s.createdAt && <div style={{ fontSize: "0.68rem", color: "#9ca3af", marginTop: 3 }}>{formatDate(s.createdAt)}</div>}
              </div>
            </div>
          ))}
        </div>

        {/* Create */}
        <div style={{
          padding: isMobile ? "12px 14px" : "14px 20px", borderTop: "1.5px solid #f1f5f9", background: "#fafafa", flexShrink: 0,
          paddingBottom: isMobile ? "max(12px, env(safe-area-inset-bottom))" : undefined,
        }}>
          {!showForm ? (
            <button onClick={() => { setShowForm(true); setJustCreatedId(null); }}
              style={{ width: "100%", background: "linear-gradient(135deg,#6366f1,#4f46e5)", border: "none", borderRadius: 10, padding: "10px 14px", fontWeight: 700, fontSize: "0.86rem", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7 }}>
              <i className="bi bi-plus-lg"></i>Yeni Konu Ekle
            </button>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span>Konu Adı *</span><span style={{ fontWeight: 500 }}>{name.length}/{NAME_MAX}</span>
                </label>
                <input ref={nameRef} value={name} maxLength={NAME_MAX} onChange={(e) => setName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") handleCreate(); if (e.key === "Escape") resetForm(); }}
                  placeholder="ör. Ödeme Sorunu" style={inp} />
              </div>
              <div>
                <label style={{ fontSize: "0.7rem", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <span>Açıklama</span><span style={{ fontWeight: 500 }}>{description.length}/{DESC_MAX}</span>
                </label>
                <textarea rows={2} value={description} maxLength={DESC_MAX} onChange={(e) => setDescription(e.target.value)}
                  placeholder="Opsiyonel" style={{ ...inp, resize: "vertical" }} />
              </div>
              {formError && (
                <div style={{ padding: "8px 12px", borderRadius: 8, background: "#fef2f2", border: "1px solid #fecaca", color: "#991b1b", fontSize: "0.8rem" }}>
                  <i className="bi bi-exclamation-circle me-1"></i>{formError}
                </div>
              )}
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={resetForm} disabled={saving}
                  style={{ flex: 1, background: "#fff", border: "1.5px solid #e5e7eb", borderRadius: 10, padding: "9px 0", fontWeight: 700, fontSize: "0.85rem", color: "#374151", cursor: "pointer" }}>
                  Vazgeç
                </button>
                <button onClick={handleCreate} disabled={saving || !name.trim()}
                  style={{
                    flex: 2, border: "none", borderRadius: 10, padding: "9px 0", fontWeight: 700, fontSize: "0.85rem",
                    background: saving || !name.trim() ? "#e5e7eb" : "linear-gradient(135deg,#6366f1,#4f46e5)",
                    color: saving || !name.trim() ? "#9ca3af" : "#fff",
                    cursor: saving || !name.trim() ? "not-allowed" : "pointer",
                    display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                  }}>
                  {saving ? <><span className="spinner-border spinner-border-sm" /> Kaydediliyor…</> : <><i className="bi bi-check-lg"></i> Konuyu Oluştur</>}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
